/**
 * shared/lib/security-posture/hardware-route.ts
 *
 * The ONE derivation of whether an account's hardware-key credentials can
 * currently reach Level 3, fresh (AC-26, spec module 02 §3 "Hardware route,
 * one derivation"). Moved verbatim from `client/src/app/aal3-route-status.ts`
 * — that file now imports both the type and the function rather than
 * defining them. A later server-side reader (`ladder-composer/move-facts.ts`,
 * Stage 2) needs the identical logic over the server's own credential rows,
 * so this lives in `shared/` rather than in either side: pure and
 * runtime-agnostic, no import from `client/` or `server/`, no DOM, no I/O
 * (see `shared/lib/security-posture/index.ts`).
 *
 * HONEST FRAMING (Friction Rule clause 2), carried over from the file this
 * moved out of:
 *  - Three-valued honesty. An unresolved read (`credentials === undefined`)
 *    is `"unknown"`, never a "no" — a caller must not tell someone their key
 *    doesn't qualify because a fetch failed. A credential list whose rows
 *    carry no `certified` field at all (a server predating the projection)
 *    is also `"unknown"`, for the same reason.
 *  - `"check-pending"` is a DISTINCT state from `"uncertified-only"`, not a
 *    shade of it. A key whose FIDO MDS attestation check never actually RAN
 *    (the registry was unreachable at enrolment; the deferred
 *    re-verification sweep — server/logic/webauthn/attestation-reverify.ts —
 *    still holds its retained evidence) carries `certified: false` for a
 *    reason that has nothing to do with the key. Reporting that as an
 *    assessed-and-failed verdict both asserts a check that never happened AND
 *    sends someone to buy a second key they may not need.
 *
 * Neither this type nor this function gates anything by itself; every real
 * decision is re-made server-side at the point of use.
 */

/**
 * The five routes as a closed TUPLE — the single source both the type below
 * and `tier-ladder/moves-guard.ts`'s runtime narrowing are built from (final
 * fix round, code-review Important 3: the guard used to restate this list as
 * its own `Set<unknown>`, so a sixth route added here would have type-checked
 * everywhere while silently failing validation at the wire boundary). Order
 * is documentation only; nothing reads it positionally.
 *
 * WHAT EACH VALUE MEANS (the per-member docs this tuple replaced — the union
 * below is derived, so there is nowhere else for them to live):
 *  - `unknown` — not readable right now; say nothing either way.
 *  - `none` — no credentials at all.
 *  - `check-pending` — a key's FIDO certification was never actually
 *    established: the registry was unreachable when it was enrolled, and the
 *    deferred re-verification sweep (server/logic/webauthn/
 *    attestation-reverify.ts) still holds its retained evidence.
 *    `mds_verified` is `false` on that row, but ONLY because the check has
 *    not run — not because it ran and failed. A DISTINCT state, not a shade
 *    of `uncertified-only`, precisely because the copy differs in the
 *    direction that matters (Friction Rule clause 2): telling someone "none
 *    of your keys is certified" when the truth is "we could not reach the
 *    registry yet" both asserts a check that never happened AND sends them to
 *    buy another key they may not need.
 *  - `uncertified-only` — keys are enrolled, they WERE assessed, and none of
 *    them is certified.
 *  - `certified` — at least one certified key exists, so this session simply
 *    didn't use it.
 *  - `master-held` (#3268, review round 1 MAJOR 2) — the account's designated
 *    master is incident-held AND excluded by the offerable-credential filter
 *    (an unheld credential survives). Mirrors `step-up/begin.ts`'s
 *    `HELD_MASTER_USE_TRANSFER`: every AAL3 ceremony — hardware OR dual — is
 *    master-pinned, so NONE can complete while this holds, regardless of what
 *    `certified`/`pendingReverification` would otherwise say. Checked BEFORE
 *    every other verdict.
 */
export const HARDWARE_ROUTES = [
  "unknown",
  "none",
  "check-pending",
  "uncertified-only",
  "certified",
  "master-held",
] as const;

/** Whether the account holds a key that can reach Level 3, fresh. Derived
 *  from `HARDWARE_ROUTES` — never a second hand-typed list. */
export type HardwareRoute = (typeof HARDWARE_ROUTES)[number];

/**
 * The minimal shape `resolveHardwareRoute` reads off a credential row.
 * STRUCTURAL on purpose: this file is `shared/`, so it may not import either
 * side's DTO (the client's `CredentialCertification`, a future server-side
 * projection row) — every caller just passes objects wide enough to satisfy
 * this, which plain TypeScript structural typing accepts with no cast.
 */
export interface HardwareRouteCredential {
  revoked?: boolean;
  /** Server-derived `mds_verified`. Absent on a server predating the
   *  projection — treated as unreadable, never as `false`. */
  certified?: boolean;
  /**
   * Still queued in the deferred FIDO MDS re-verification sweep. `true`
   * means certification has NOT been established either way — the registry
   * was unreachable when it was enrolled and the retained evidence is still
   * waiting on the sweep.
   */
  pendingReverification?: boolean;
  /**
   * Held after a closed incident (AC-8, #3099) — display only. #3268: a held
   * key is refused at every real ceremony (`step-up/begin.ts`,
   * `evaluateIncidentHold`) while an unheld one survives, so it must not make
   * this route read `"certified"` when the only certified key is one no
   * ceremony will actually offer. Absent/`undefined` reads as not held —
   * every caller predating this field (including every server-side row,
   * which never sets it) keeps its exact prior behaviour.
   */
  incidentHeld?: boolean;
  /**
   * Server-projected: this credential is the account's designated master
   * (ledger #86). #3268 review round 1 MAJOR 2: needed here so this file can
   * detect the SAME "master held, no route at all" case
   * `step-up/begin.ts` throws `HELD_MASTER_USE_TRANSFER` for — see
   * `isMasterHeldWithSurvivor`. Absent reads as not-master.
   */
  isMaster?: boolean;
}

/**
 * Mirrors `offerableForCeremony` (`server/logic/recovery/incident-hold.ts`)
 * for DISPLAY ONLY — never an enforcement input; the server re-evaluates the
 * hold at every real ceremony. A held key drops out while an unheld key
 * survives; when EVERY key is held none are excluded (the no-route-back
 * proviso) — a route that would actually still work must not be hidden.
 *
 * EXPORTED (#3268 review round 1 MINOR 8) — this is now the ONE
 * implementation; `client/src/app/incident-held-credentials.ts`, which used to
 * hold a byte-identical private copy for the client-only two-key resolvers,
 * is deleted, and those resolvers import this one instead.
 */
export function offerableCredentials<T extends { incidentHeld?: boolean }>(active: readonly T[]): readonly T[] {
  const unheld = active.filter((c) => c.incidentHeld !== true);
  return unheld.length > 0 ? unheld : active;
}

/**
 * #3268 review round 1 MAJOR 2: whether the account's designated master is
 * incident-held AND excluded by `offerableCredentials` above — i.e. some
 * OTHER unrevoked credential in `active` is unheld and survives. Mirrors
 * `step-up/begin.ts`'s own `heldMaster && !active.includes(heldMaster)` test
 * exactly (same two facts: is there a held master, does it get filtered out).
 *
 * `active` MUST be the unrevoked set BEFORE `offerableCredentials` is applied
 * — same input begin.ts's own two reads share. When every credential is held
 * (the no-route-back proviso), the master is never excluded by the filter, so
 * this correctly reads `false` and the ordinary hardware/two-key logic below
 * still applies to the held master (it is still `offerableCredentials`'s only
 * survivor).
 */
export function isMasterHeldWithSurvivor<T extends { incidentHeld?: boolean; isMaster?: boolean }>(
  active: readonly T[],
): boolean {
  const master = active.find((c) => c.isMaster === true);
  if (!master || master.incidentHeld !== true) return false;
  return active.some((c) => c !== master && c.incidentHeld !== true);
}

/**
 * Pure, so the three-valued logic is directly testable.
 *
 * `undefined` in, `"unknown"` out. A credential list whose rows carry no
 * `certified` field at all is also `"unknown"` — an older server that cannot
 * answer the question must not be read as answering "no".
 */
export function resolveHardwareRoute(
  credentials: HardwareRouteCredential[] | undefined,
): HardwareRoute {
  if (credentials === undefined) return "unknown";
  const active = credentials.filter((c) => c.revoked !== true);
  if (active.length === 0) return "none";
  // #3268 review round 1 MAJOR 2: checked BEFORE every other verdict — a
  // held master blocks EVERY AAL3 ceremony (begin.ts is master-pinned for
  // both bases), so nothing below may report a route that would actually
  // throw `HELD_MASTER_USE_TRANSFER`.
  if (isMasterHeldWithSurvivor(active)) return "master-held";
  // #3268: judge the hold BEFORE the certified/pending/uncertified checks, over
  // the full active set — an incident-held key must not report "certified" (or
  // any other verdict) while an unheld key survives to make it unofferable.
  const offerable = offerableCredentials(active);
  if (offerable.some((c) => c.certified === true)) return "certified";
  // BEFORE the "not certified" verdict, never after: a queued credential's
  // `certified` is `false` only because the check has not run. Reporting it as
  // assessed-and-failed asserts a check that never happened AND tells someone
  // to go buy another key when the one they hold may well qualify once the
  // sweep lands (Friction Rule clause 2).
  if (offerable.some((c) => c.pendingReverification === true)) return "check-pending";
  if (offerable.some((c) => c.certified === false)) return "uncertified-only";
  return "unknown";
}
